import { z } from 'zod';
import type { Numeric, NumericValue } from '@/utils/numeric';
import type { PickWindowSource } from '@/types/cultivar';

// =============================================================================
// GEOJSON TYPES
// =============================================================================

export interface GeoJSONPolygon {
  type: 'Polygon';
  coordinates: number[][][]; // [[[lng, lat], [lng, lat], ...]]
}

export interface LatLng {
  lat: number;
  lng: number;
}

// =============================================================================
// CORE ENTITY INTERFACES
// =============================================================================

export interface Farm {
  id: number;
  organisation_id: number;
  name: string;
  description: string | null;
  location: string | null;
  boundary_geometry: GeoJSONPolygon;
  center_point: LatLng | null;
  total_area_hectares: Numeric;
  /**
   * The climate area this farm sits in. Evaluations recorded here inherit it
   * for maturity attribution — deriveObservedRecords resolves a region through
   * COALESCE(evaluation, farm), so setting it once covers every future visit.
   */
  growing_region_id: number | null;
  /**
   * Short code, 2 to 6 letters or digits, unique in the organisation. The
   * farm's purchase orders are numbered with it: PO-<code>-<org>-<seq>.
   */
  code?: string | null;
  /** Its cultivar areas are planned by row and position, as nursery blocks are. */
  plan_rows_by_position?: boolean;
  active: boolean;
  created_by_user_id: number | null;
  created_at: string;
  updated_at: string;

  // Joined data
  growing_region_name?: string | null;
  /** Only on the response to the save that archived the farm: the hardware it let go. */
  released?: FarmReleased;
  block_count?: NumericValue;
  cultivar_area_count?: NumericValue;
}

export interface Block {
  id: number;
  farm_id: number;
  farm_name?: string;
  organisation_id: number;
  name: string;
  description: string | null;
  boundary_geometry: GeoJSONPolygon;
  center_point: LatLng | null;
  area_hectares: Numeric;
  /** Where the block sits in the farm's lists and walk order. Null: after the sequenced ones, by name. */
  sort_order?: number | null;
  active: boolean;
  created_by_user_id: number | null;
  created_at: string;
  updated_at: string;

  // Joined data
  cultivar_area_count?: NumericValue;
}

export interface CultivarArea {
  id: number;
  block_id: number;
  block_name?: string;
  farm_id: number;
  farm_name?: string;
  organisation_id: number;
  name: string;
  // Primary cultivar is optional — when null the area relies on the planting
  // plan (planting_pattern + row overrides) to describe what's planted.
  cultivar_id: number | null;
  cultivar_name?: string;
  cultivar_trade_name?: string;
  boundary_geometry: GeoJSONPolygon;
  center_point: LatLng | null;
  area_hectares: Numeric;
  tree_count: number | null;
  row_spacing_meters: Numeric;
  tree_spacing_meters: Numeric;
  planting_date: string | null;
  // Composite tree. rootstock_cultivar_id is the real reference; `rootstock`
  // is the free-text fallback for blocks whose stock is unknown or
  // unregistered. If the id is set it wins.
  rootstock: string | null;
  rootstock_cultivar_id: number | null;
  rootstock_cultivar_name?: string | null;
  rootstock_cultivar_trade_name?: string | null;
  rootstock_protection_status?: string | null;
  interstem_cultivar_id: number | null;
  interstem_cultivar_name?: string | null;
  interstem_cultivar_trade_name?: string | null;
  interstem_protection_status?: string | null;
  graft_union_height_mm: number | null;
  cultivar_protection_status?: string | null;
  irrigation_zone: string | null;
  training_system: string | null;
  tree_supplier: string | null;
  notes: string | null;
  active: boolean;
  created_by_user_id: number | null;
  created_at: string;
  updated_at: string;

  // The harvest window this area is heading into, joined on by the API. Read
  // by the spray module: a spray whose withholding period runs past
  // upcoming_earliest_harvest_date is held for a Spray Admin decision.
  upcoming_harvest_window_id?: number | null;
  upcoming_harvest_season_year?: number | null;
  upcoming_earliest_harvest_date?: string | null;
  upcoming_harvest_end_date?: string | null;
  upcoming_harvest_status?: HarvestWindowStatus | null;

  // Planting plan (populated when present on the row)
  first_row_start_point?: [number, number] | null;  // [lng, lat]
  first_row_end_point?: [number, number] | null;    // [lng, lat]
  row_bearing_degrees?: Numeric;
  row_count?: number | null;
  planting_pattern?: RowGroup[] | null;
  default_pollinators?: Pollinator[] | null;

  /** The farm plans by row and position: the area shows the row planner. */
  farm_plans_by_position?: boolean;
}

// =============================================================================
// HARVEST WINDOWS
// =============================================================================

/**
 * estimate  — a planning figure, from the cultivar's maturity history or a guess
 * confirmed — the grower has committed to it
 * actual    — first pick happened on this date
 */
export type HarvestWindowStatus = 'estimate' | 'confirmed' | 'actual';

/**
 * When a cultivar area is due to be picked, for one season.
 *
 * Held per season rather than as a single date on the area: a date that has
 * quietly gone stale reads exactly like a live one, and the spray module's
 * withholding-period check is only as trustworthy as the date under it.
 */
export interface HarvestWindow {
  id: number;
  cultivar_area_id: number;
  organisation_id: number;
  /** The calendar year the window OPENS — southern harvests run across new year. */
  season_year: number;
  earliest_harvest_date: string;   // YYYY-MM-DD
  expected_end_date: string | null;
  status: HarvestWindowStatus;
  /** 'bins' when written from the first and last bin picked; a person saving it makes it 'manual'. */
  source?: 'manual' | 'bins';
  notes: string | null;
  created_by_user_id: number | null;
  created_by_name?: string | null;
  created_at: string;
  updated_at: string;
}

export interface HarvestWindowFormData {
  season_year: number;
  earliest_harvest_date: string;
  expected_end_date?: string | null;
  status?: HarvestWindowStatus;
  notes?: string | null;
}

/** A proposal derived from the cultivar's maturity records. Saves nothing. */
export interface HarvestWindowSuggestion {
  season_year: number;
  earliest_harvest_date: string;
  expected_end_date: string | null;
  /** What the end date rests on — see `PickWindowSource`. Absent from older servers. */
  window_source?: PickWindowSource;
  status: HarvestWindowStatus;
  based_on: {
    cultivar_name: string | null;
    region_name: string | null;
    season_year: number;
  };
}

// =============================================================================
// PLANTING PLAN TYPES
// =============================================================================

export interface RowGroup {
  cultivar_id: number | null;
  row_count: number;
  // Client-side hydration
  cultivar_name?: string | null;
  cultivar_trade_name?: string | null;
}

export interface Pollinator {
  cultivar_id: number;
  first_position: number; // 1-based tree index within the row
  interval: number;       // spacing in trees between pollinators
  label?: string | null;
  // Client-side hydration
  cultivar_name?: string | null;
  cultivar_trade_name?: string | null;
}

export interface RowOverride {
  row_number: number;
  primary_cultivar_id: number | null;
  primary_cultivar_name?: string | null;
  primary_cultivar_trade_name?: string | null;
  pollinators: Pollinator[] | null;
  notes: string | null;
}

export interface GeneratedRow {
  row_number: number;
  start_point: [number, number];  // [lng, lat]
  end_point: [number, number];    // [lng, lat]
  length_meters: number;
  tree_count: number;
  primary_cultivar_id: number | null;
  pollinators: Pollinator[];
  sources: { primary: string | null; pollinators: string | null };
}

export interface PlantingPlanSummaryEntry {
  cultivar_id: number;
  primary_trees: number;
  pollinator_trees: number;
  total_trees: number;
}

export interface PlantingPlanData {
  first_row_start_point: [number, number] | null;
  first_row_end_point: [number, number] | null;
  row_bearing_degrees: number | null;
  row_count: number | null;
  row_spacing_meters: number | null;
  tree_spacing_meters: number | null;
  planting_pattern: RowGroup[];
  default_pollinators: Pollinator[];
  primary_cultivar_id: number | null;
  /** Rows actually laid out: the entered row_count or, when none is entered,
   *  as many as fit the boundary. */
  effective_row_count: number;
  row_count_source: 'entered' | 'fitted' | null;
  /** How many rows at this spacing fit the boundary (null without one). */
  fitted_row_count: number | null;
  total_trees: number;
}

export interface PlantingPlanCultivar {
  id: number;
  name: string;
  trade_name: string | null;
}

export interface PlantingPlan {
  plan: PlantingPlanData;
  overrides: RowOverride[];
  rows: GeneratedRow[];
  summary: PlantingPlanSummaryEntry[];
  cultivars: PlantingPlanCultivar[];
}

export interface PlantingPlanSaveData {
  first_row_start_point?: [number, number] | null;
  first_row_end_point?: [number, number] | null;
  row_count?: number | null;
  row_spacing_meters?: number | null;
  tree_spacing_meters?: number | null;
  planting_pattern?: RowGroup[] | null;
  default_pollinators?: Pollinator[] | null;
}

/** An unsaved plan laid out by the server. `area_id` applies that area's saved
 *  per-row overrides so the preview matches what saving would produce. */
export interface PlantingPlanPreviewRequest extends PlantingPlanSaveData {
  boundary_geometry: GeoJSONPolygon | null;
  cultivar_id?: number | null;
  area_id?: number | null;
}

// =============================================================================
// ROWS AND POSITIONS (a farm that plans by position)
// =============================================================================

/** A stretch of trees in a row: Row 5 · S10 = Gala, 40 trees. */
export interface OrchardPosition {
  id: number;
  cultivar_area_id: number;
  row_number: number;
  sequence_number: number;
  cultivar_id: number;
  cultivar_name: string;
  cultivar_trade_name: string | null;
  tree_count: number;
  rootstock_cultivar_id: number | null;
  rootstock_cultivar_name: string | null;
  rootstock_cultivar_trade_name: string | null;
  rootstock: string | null;
  planting_date: string | null;
  notes: string | null;
}

export interface OrchardPositionRow {
  row_number: number;
  trees: number;
  positions: OrchardPosition[];
}

export interface OrchardPositionCultivar {
  cultivar_id: number;
  cultivar_name: string | null;
  cultivar_trade_name: string | null;
  trees: number;
  positions: number;
  rows: number;
}

export interface OrchardPositionPlan {
  cultivar_area_id: number;
  rows: OrchardPositionRow[];
  summary: OrchardPositionCultivar[];
  total_trees: number;
  row_count: number;
  /** On an add: how many were new, taken over, or kept as they were. */
  added?: number;
  replaced?: number;
  skipped?: number;
  /** On a delete: how many went. */
  removed?: number;
}

export interface OrchardPositionDetails {
  cultivar_id?: number;
  tree_count?: number;
  rootstock_cultivar_id?: number | null;
  rootstock?: string | null;
  planting_date?: string | null;
  notes?: string | null;
}

export interface AddOrchardPositionsData extends OrchardPositionDetails {
  start_row: number;
  end_row?: number | null;
  sequences: number[];
  cultivar_id: number;
  tree_count: number;
  /** Positions already planted take the new cultivar and details. */
  replace?: boolean;
}

export interface UpdateOrchardPositionData extends OrchardPositionDetails {
  row_number?: number;
  sequence_number?: number;
}

// =============================================================================
// FORM DATA TYPES
// =============================================================================

export interface FarmFormData {
  name: string;
  description?: string;
  location?: string;
  boundary_geometry: GeoJSONPolygon;
  center_point?: LatLng;
  total_area_hectares?: number;
  growing_region_id?: number | null;
  /** Blank on a new farm has the server make one from the name; blank on an edit keeps the code. */
  code?: string;
  plan_rows_by_position?: boolean;
  active?: boolean;
}

export interface BlockFormData {
  farm_id: number;
  name: string;
  description?: string;
  boundary_geometry: GeoJSONPolygon;
  center_point?: LatLng;
  area_hectares?: number;
  sort_order?: number | null;
  active?: boolean;
}

export interface CultivarAreaFormData {
  block_id: number;
  farm_id: number;
  name: string;
  cultivar_id: number | null;
  boundary_geometry: GeoJSONPolygon;
  center_point?: LatLng;
  area_hectares?: number;
  tree_count?: number;
  row_spacing_meters?: number;
  tree_spacing_meters?: number;
  planting_date?: string;
  rootstock?: string;
  rootstock_cultivar_id?: number | null;
  interstem_cultivar_id?: number | null;
  graft_union_height_mm?: number | null;
  irrigation_zone?: string;
  training_system?: string;
  tree_supplier?: string;
  /** The supplier as an organisation; tree_supplier keeps the name when the
   *  nursery is not on the platform. */
  supplied_by_nursery_org_id?: number | null;
  /** Declared only when mapping trees that are already in the ground with no
   *  planting licence behind them. See CultivarAreaForm. */
  pre_existing_supply?: {
    nursery_org_id?: number | null;
    supplier_name?: string;
    planting_year?: number | null;
  } | null;
  notes?: string;
  active?: boolean;
  /** Planted by rows: laid out at creation, and the rows then state the tree
   *  count and spacing instead of the spacing estimate. */
  planting_plan?: PlantingPlanSaveData | null;
}

// =============================================================================
// ZOD VALIDATION SCHEMAS
// =============================================================================

export const geoJSONPolygonSchema = z.object({
  type: z.literal('Polygon'),
  coordinates: z.array(z.array(z.array(z.number()))).min(1)
});

export const latLngSchema = z.object({
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180)
});

export const farmSchema = z.object({
  name: z.string().min(1, 'Name is required').max(255),
  description: z.string().optional(),
  location: z.string().max(255).optional(),
  boundary_geometry: geoJSONPolygonSchema,
  center_point: latLngSchema.optional(),
  total_area_hectares: z.number().positive().optional(),
  active: z.boolean().optional()
});

export const blockSchema = z.object({
  farm_id: z.number().int().positive(),
  name: z.string().min(1, 'Name is required').max(255),
  description: z.string().optional(),
  boundary_geometry: geoJSONPolygonSchema,
  center_point: latLngSchema.optional(),
  area_hectares: z.number().positive().optional(),
  active: z.boolean().optional()
});

export const cultivarAreaSchema = z.object({
  block_id: z.number().int().positive(),
  farm_id: z.number().int().positive(),
  name: z.string().min(1, 'Name is required').max(255),
  cultivar_id: z.number().int().positive().nullable(),
  boundary_geometry: geoJSONPolygonSchema,
  center_point: latLngSchema.optional(),
  area_hectares: z.number().positive().optional(),
  tree_count: z.number().int().min(0).optional(),
  row_spacing_meters: z.number().positive().optional(),
  tree_spacing_meters: z.number().positive().optional(),
  planting_date: z.string().optional(),
  rootstock: z.string().max(255).optional(),
  rootstock_cultivar_id: z.number().int().positive().nullable().optional(),
  interstem_cultivar_id: z.number().int().positive().nullable().optional(),
  graft_union_height_mm: z.number().int().min(0).max(2000).nullable().optional(),
  irrigation_zone: z.string().max(100).optional(),
  training_system: z.string().max(100).optional(),
  tree_supplier: z.string().max(255).optional(),
  supplied_by_nursery_org_id: z.number().int().positive().nullable().optional(),
  pre_existing_supply: z
    .object({
      nursery_org_id: z.number().int().positive().nullable().optional(),
      supplier_name: z.string().max(255).optional(),
      planting_year: z.number().int().min(1900).max(2200).nullable().optional()
    })
    .nullable()
    .optional(),
  notes: z.string().optional(),
  active: z.boolean().optional()
});

// =============================================================================
// PAGINATION TYPE
// =============================================================================

export interface Pagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

// =============================================================================
// API RESPONSE TYPES
// =============================================================================

export interface FarmsResponse {
  farms: Farm[];
  pagination: Pagination;
}

export interface BlocksResponse {
  blocks: Block[];
  pagination: Pagination;
}

export interface CultivarAreasResponse {
  areas: CultivarArea[];
  pagination: Pagination;
}

/** Irrigation hardware an archive frees for use on another farm (orchardService.releaseIrrigation). */
export interface FarmReleased {
  /** Bridges / field controllers, now unassigned. */
  controllers: number;
  /** Soil sensors, now unassigned. */
  sensors: number;
  /** Network-map nodes whose relay, sensor or pump-equipment link was cleared. */
  network_links: number;
}

/** What is attached to a farm — shown before archiving, and on an archived farm. */
export interface FarmAssetSummary extends FarmReleased {
  blocks: number;
  cultivar_areas: number;
  equipment: number;
  weather_stations: number;
  chemical_locations: number;
}

export interface FarmMapData {
  farm: Farm;
  blocks: Block[];
  cultivar_areas: CultivarArea[];
}

// =============================================================================
// CONSTANTS
// =============================================================================

export const TRAINING_SYSTEMS = [
  'V-Trellis',
  'Spindle',
  'Espalier',
  'Central Leader',
  'Open Vase',
  'Pergola',
  'Bush',
  'Cordon',
  'Other'
] as const;

export type TrainingSystem = typeof TRAINING_SYSTEMS[number];

// =============================================================================
// CROP LOAD COUNTS — sample trees measured and counted, scaled to a crop
// estimate. Arithmetic: HHNodeServer utils/cropLoad.js (mirrored in
// src/utils/cropLoad.ts for the preview).
// =============================================================================

export type TrunkMeasure = 'circumference_cm' | 'diameter_mm';
export type CropLoadStage = 'fruit_set' | 'after_thinning' | 'pre_harvest';

export interface CropLoadSample {
  row_label: string | null;
  tree_label: string | null;
  /** Trunk circumference in cm or diameter in mm, per the count's trunk_measure. */
  trunk_value: number | null;
  /** Fruit counted on the whole tree, or on one limb of limb_diameter_mm. */
  counted_on: 'tree' | 'limb';
  limb_diameter_mm: number | null;
  /** Null = a trunk measurement only. */
  fruit_count: number | null;
}

export interface CropLoadResult {
  samples_counted: number;
  trunks_measured: number;
  fruit_counted: number;
  mean_tcsa_cm2: number | null;
  fruit_per_cm2: number | null;
  fruit_per_cm2_sd: number | null;
  fruit_per_tree: number | null;
  tree_count: number | null;
  loss_pct: number;
  fruit_weight_g: number | null;
  bin_kg: number | null;
  total_fruit: number | null;
  tonnes: number | null;
  tonnes_per_ha: number | null;
  bins: number | null;
  /** 95 % range on the trees counted; null with fewer than two. */
  range: {
    fruit_per_tree: [number, number];
    tonnes: [number, number] | null;
    bins: [number, number] | null;
  } | null;
  warnings: string[];
}

export interface CropLoadCount {
  id: number;
  cultivar_area_id: number;
  season_year: number;
  counted_on: string;
  stage: CropLoadStage | null;
  trunk_measure: TrunkMeasure;
  fruit_weight_g: number | null;
  bin_kg: number | null;
  loss_pct: number;
  notes: string | null;
  applied_at: string | null;
  applied_by_name: string | null;
  created_by_name: string | null;
  created_at: string;
  updated_at: string;
  samples: CropLoadSample[];
  result: CropLoadResult;
}

export interface CropLoadList {
  area: { id: number; name: string; tree_count: number | null; area_hectares: number | null };
  defaults: {
    bin_kg: number | null;
    bin_name: string | null;
    /** Median of the cultivar's recorded fruit weight readings. */
    fruit_weight_g: number | null;
    fruit_weight_readings: number;
    season_start_month: number;
  };
  counts: CropLoadCount[];
  /** Farm Jobs creator/admin: may make a count the season's crop estimate. */
  can_apply: boolean;
}

export interface CropLoadFormData {
  counted_on: string;
  stage: CropLoadStage | null;
  trunk_measure: TrunkMeasure;
  fruit_weight_g: number | null;
  bin_kg: number | null;
  loss_pct: number;
  notes: string | null;
  samples: CropLoadSample[];
}
